import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, AlarmClock, FileSpreadsheet } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useWorkspace } from '@/hooks/useWorkspace';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/components/ui/toaster';
import { hrRequestService, type HrRequestType, type HrRequestStatus } from '@/services/hr-request.service';
import { formatRp } from '@/lib/currency';
import { fmtDate } from '@/lib/time';
import OvertimeFormSheet from '@/components/OvertimeFormSheet';

const TYPE_LABEL_KEY: Record<HrRequestType, string> = {
    overtime: 'ot.type.overtime',
    lateness_excuse: 'ot.type.lateness_excuse',
    reimbursement: 'ot.type.reimbursement',
    visit: 'ot.type.visit',
    sick_leave: 'ot.type.sick_leave',
    permission_leave: 'ot.type.permission_leave',
    annual_leave: 'ot.type.annual_leave',
    maternity_leave: 'ot.type.maternity_leave',
};

const STATUS_STYLE: Record<HrRequestStatus, { bg: string; color: string; labelKey: string }> = {
    pending:   { bg: '#fef9e7', color: '#a07b00', labelKey: 'ot.status.pending' },
    approved:  { bg: '#e8f3e8', color: '#3d8b37', labelKey: 'ot.status.approved' },
    rejected:  { bg: '#fee2e2', color: '#b91c1c', labelKey: 'ot.status.rejected' },
    cancelled: { bg: '#f3f4f6', color: '#6b7280', labelKey: 'ot.status.cancelled' },
};

export default function OvertimePage() {
    const { t } = useI18n();
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [list, setList] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [filterType, setFilterType] = useState<HrRequestType | ''>('');
    const [filterStatus, setFilterStatus] = useState<HrRequestStatus | ''>('');

    useEffect(() => {
        if (!currentWorkspace) return;
        load();
    }, [currentWorkspace?._id, filterType, filterStatus]);

    const load = async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        try {
            const res = await hrRequestService.list({
                workspaceId: currentWorkspace._id,
                scope: 'mine',
                type: (filterType || undefined) as any,
                status: (filterStatus || undefined) as any,
            });
            setList((res.data as any[]) || []);
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || 'Gagal memuat' });
        } finally {
            setLoading(false);
        }
    };

    if (!currentWorkspace) {
        return <div className="px-4 pt-12 max-w-md mx-auto text-center text-sm text-gray-500">{t('common.loading')}</div>;
    }

    return (
        <div className="px-4 pt-3 max-w-md mx-auto space-y-3">
            <header className="pt-2 mb-2">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center">
                            <AlarmClock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <h1 className="text-lg font-black text-gray-900">{t('ot.title')}</h1>
                            <p className="text-[11px] text-gray-500 max-w-[220px] leading-tight">{t('ot.subtitle')}</p>
                        </div>
                    </div>
                    <Button size="sm" onClick={() => setShowForm(true)}>
                        <Plus className="h-4 w-4 mr-1" /> {t('ot.btn_new')}
                    </Button>
                </div>
            </header>

            <div className="grid grid-cols-2 gap-2">
                <Select value={filterType || 'all'} onValueChange={(v) => setFilterType((v === 'all' ? '' : v) as any)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('ot.filter_all_types')}</SelectItem>
                        {Object.entries(TYPE_LABEL_KEY).map(([k, lk]) => <SelectItem key={k} value={k}>{t(lk)}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus((v === 'all' ? '' : v) as any)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('ot.filter_all_status')}</SelectItem>
                        {Object.entries(STATUS_STYLE).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {loading && <p className="text-sm text-gray-400 italic px-1">{t('common.loading')}</p>}

            {!loading && list.length === 0 && (
                <div className="bg-white rounded-2xl p-8 text-center" style={{ border: '1px dashed #e0d6cc' }}>
                    <div className="h-12 w-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
                        <FileSpreadsheet className="h-5 w-5 text-amber-600" />
                    </div>
                    <p className="text-sm text-gray-500 max-w-[260px] mx-auto">{t('ot.empty')}</p>
                </div>
            )}

            <div className="space-y-2">
                {list.map((r) => (
                    <button
                        key={r.id}
                        onClick={() => navigate(`/lembur/${r.id}`)}
                        className="w-full text-left bg-white rounded-xl p-3.5 hover:bg-gray-50 transition flex items-start gap-3"
                        style={{ border: '1px solid #e0d6cc' }}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-[11px] font-bold text-gray-700">{t(TYPE_LABEL_KEY[r.type as HrRequestType])}</span>
                                <StatusPill status={r.status} />
                            </div>
                            <p className="text-sm font-medium text-gray-900 truncate">{r.payload?.reason || '—'}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">
                                {fmtDate(r.fromAt)}{r.toAt ? ` – ${fmtDate(r.toAt)}` : ''} · {r.team?.name || '—'}
                            </p>
                        </div>
                        {r.amount > 0 && (
                            <span className="text-sm font-bold text-gray-700 shrink-0">{formatRp(r.amount)}</span>
                        )}
                    </button>
                ))}
            </div>

            <OvertimeFormSheet open={showForm} onOpenChange={setShowForm} onCreated={() => { setShowForm(false); load(); }} />
        </div>
    );
}

function StatusPill({ status }: { status: HrRequestStatus }) {
    const { t } = useI18n();
    const s = STATUS_STYLE[status];
    return (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.color }}>
            {t(s.labelKey)}
        </span>
    );
}
